/* eslint-disable */

// hierarchyUtils.js
//--------------------------------------------------------------
// 1) Agregação de status
//--------------------------------------------------------------
export function aggregateStatus(statuses = []) {
  if (statuses.includes("red")) return "red";
  if (statuses.includes("yellow")) return "yellow";
  return "green";
}

//--------------------------------------------------------------
// 2) Build hierarchy
//--------------------------------------------------------------
export function buildHierarchyFromInitiatives(initiatives) {
  const branchMap = {};
  const processedInitiatives = new Set(); // Track para evitar duplicações

  initiatives.forEach((init) => {
    // Usar id como chave única, ou fallback para initiativeName
    const initiativeKey = init.id || init.initiativeName;
    
    // Skip se já processamos esta iniciativa
    if (processedInitiatives.has(initiativeKey)) {
      return;
    }
    processedInitiatives.add(initiativeKey);

    /* -------- Branch -------- */
    if (!branchMap[init.branch]) {
      branchMap[init.branch] = {
        name: init.branch,
        status: "gray", // ← default neutro
        kpiValue: 0,
        kpiCapturedYTD: 0,
        kpiTargetYTD: 0,
        initiatives: 0,
        children: {},
      };
    }
    const branchObj = branchMap[init.branch];

    /* -------- Workfront -------- */
    if (!branchObj.children[init.workfront]) {
      branchObj.children[init.workfront] = {
        name: init.workfront,
        status: "gray", // ← default neutro
        kpiValue: 0,
        kpiCapturedYTD: 0,
        kpiTargetYTD: 0,
        initiatives: 0,
        children: {},
      };
    }
    const wfObj = branchObj.children[init.workfront];

    /* -------- Initiative (folha) -------- */
    // Usar id como chave para garantir unicidade
    wfObj.children[initiativeKey] = {
      id: init.id,
      name: init.initiativeName,
      status: (init.status || "gray").toLowerCase(), // ↓
      kpiValue: init.kpiValue || 0,
      kpiCapturedYTD: init.kpiCapturedYTD || 0,
      kpiTargetYTD: init.kpiTargetYTD || 0,
      initiatives: 1,
      leader: init.leader || "",
      children: [],
    };
  });

  /* --------- Agregações bottom-up --------- */
  const branchArray = Object.values(branchMap);

  branchArray.forEach((branchObj) => {
    const workfrontArray = Object.values(branchObj.children);

    workfrontArray.forEach((wfObj) => {
      const initArray = Object.values(wfObj.children);

      wfObj.kpiValue = initArray.reduce((s, n) => s + n.kpiValue, 0);
      wfObj.kpiCapturedYTD = initArray.reduce(
        (s, n) => s + n.kpiCapturedYTD,
        0
      );
      wfObj.kpiTargetYTD = initArray.reduce((s, n) => s + n.kpiTargetYTD, 0);
      wfObj.initiatives = initArray.length;
      wfObj.status = aggregateStatus(initArray.map((n) => n.status));
      wfObj.children = initArray;
    });

    branchObj.kpiValue = workfrontArray.reduce((s, w) => s + w.kpiValue, 0);
    branchObj.kpiCapturedYTD = workfrontArray.reduce(
      (s, w) => s + w.kpiCapturedYTD,
      0
    );
    branchObj.kpiTargetYTD = workfrontArray.reduce(
      (s, w) => s + w.kpiTargetYTD,
      0
    );
    branchObj.initiatives = workfrontArray.reduce(
      (s, w) => s + w.initiatives,
      0
    );
    branchObj.status = aggregateStatus(workfrontArray.map((w) => w.status));
    branchObj.children = workfrontArray;
  });

  return branchArray;
}

// 3) sortDataRecursively
export function sortDataRecursively(data, sortConfig) {
  if (!sortConfig.key) return data;

  // pior → melhor
  const order = { red: 0, yellow: 1, gray: 2, green: 3 };

  const sorted = [...data].sort((a, b) => {
    if (sortConfig.key === "status") {
      const aVal = order[a.status] ?? 4;
      const bVal = order[b.status] ?? 4;
      return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
    } else {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
      }
      const aStr = (aVal || "").toString().toLowerCase();
      const bStr = (bVal || "").toString().toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    }
  });

  // recursivo
  return sorted.map((item) => ({
    ...item,
    children: item.children
      ? sortDataRecursively(item.children, sortConfig)
      : [],
  }));
}

/**
 * Utilitários para trabalhar com configurações de hierarquia
 * Reutilizável em outros componentes que precisem de hierarquia dinâmica
 */

/**
 * Busca a configuração de hierarquias do cliente
 * @returns {Promise<Object>} Configuração de hierarquias
 */
export const fetchHierarchyConfig = async () => {
  try {
    const { client } = await import('../services/axiosClient.js');
    const response = await client.get("/api/hierarchy-config/");
    
    if (response.data.success) {
      return response.data.data;
    }
    
    // Fallback para configuração padrão
    return {
      hierarchy_levels: ["branch", "workfront", "initiative"],
      level_count: 3,
      has_branch: true,
      has_workfront: true,
      has_initiative: true
    };
  } catch (error) {
    console.error("Erro ao buscar configuração de hierarquias:", error);
    // Retornar configuração padrão em caso de erro
    return {
      hierarchy_levels: ["branch", "workfront", "initiative"],
      level_count: 3,
      has_branch: true,
      has_workfront: true,
      has_initiative: true
    };
  }
};

/**
 * Verifica se um nível específico está disponível na configuração
 * @param {Array} hierarchyConfig - Configuração de hierarquias
 * @param {string} level - Nível a verificar
 * @returns {boolean} True se o nível estiver disponível
 */
export const hasHierarchyLevel = (hierarchyConfig, level) => {
  return hierarchyConfig && hierarchyConfig.includes(level);
};

/**
 * Obtém o número de níveis de hierarquia
 * @param {Array} hierarchyConfig - Configuração de hierarquias
 * @returns {number} Número de níveis
 */
export const getHierarchyLevelCount = (hierarchyConfig) => {
  return hierarchyConfig ? hierarchyConfig.length : 3;
};

/**
 * Obtém o tipo de hierarquia para um nível específico
 * @param {Array} hierarchyConfig - Configuração de hierarquias
 * @param {number} level - Nível (0-based)
 * @returns {string} Tipo de hierarquia
 */
export const getHierarchyTypeForLevel = (hierarchyConfig, level) => {
  if (!hierarchyConfig || level >= hierarchyConfig.length) {
    return null;
  }
  return hierarchyConfig[level];
};

/**
 * Gera labels para cabeçalhos baseados na configuração
 * @param {Array} hierarchyConfig - Configuração de hierarquias
 * @param {Function} t - Função de tradução
 * @returns {Array} Array de labels para cabeçalhos
 */
export const generateHierarchyHeaders = (hierarchyConfig, t) => {
  if (!hierarchyConfig || hierarchyConfig.length === 0) {
    return [t("deliverables.name")];
  }
  
  if (hierarchyConfig.length === 1) {
    return [t("deliverables.initiative")];
  }
  
  return [t("deliverables.name")];
};

/**
 * Determina se deve mostrar ícone de expansão para um nível
 * @param {Array} hierarchyConfig - Configuração de hierarquias
 * @param {number} currentLevel - Nível atual
 * @returns {boolean} True se deve mostrar ícone de expansão
 */
export const shouldShowExpandIcon = (hierarchyConfig, currentLevel) => {
  return hierarchyConfig && currentLevel < hierarchyConfig.length - 1;
};

/**
 * Calcula o padding para indentação baseado no nível
 * @param {number} level - Nível atual
 * @param {number} basePadding - Padding base (padrão: 16)
 * @returns {number} Padding calculado
 */
export const calculateIndentation = (level, basePadding = 16) => {
  return level * basePadding;
};

/**
 * Obtém estilos de texto baseados no nível da hierarquia
 * @param {number} level - Nível atual
 * @returns {Object} Estilos de texto
 */
export const getHierarchyTextStyles = (level) => {
  const baseStyles = {
    transition: "all 0.15s ease",
  };
  
  switch (level) {
    case 0:
      return {
        ...baseStyles,
        fontWeight: "600",
        fontSize: "0.8rem",
        color: "#1e293b"
      };
    case 1:
      return {
        ...baseStyles,
        fontWeight: "500",
        fontSize: "0.75rem",
        color: "#334155"
      };
    default:
      return {
        ...baseStyles,
        fontWeight: "400",
        fontSize: "0.7rem",
        color: "#475569"
      };
  }
};

/**
 * Valida se uma configuração de hierarquia é válida
 * @param {Array} hierarchyConfig - Configuração a validar
 * @returns {boolean} True se a configuração for válida
 */
export const isValidHierarchyConfig = (hierarchyConfig) => {
  if (!Array.isArray(hierarchyConfig)) {
    return false;
  }
  
  const validLevels = ["branch", "workfront", "initiative"];
  const hasInitiative = hierarchyConfig.includes("initiative");
  
  // Deve ter pelo menos iniciativa e apenas valores válidos
  return hasInitiative && hierarchyConfig.every(level => validLevels.includes(level));
};

/**
 * Normaliza uma configuração de hierarquia para garantir que seja válida
 * @param {Array} hierarchyConfig - Configuração a normalizar
 * @returns {Array} Configuração normalizada
 */
export const normalizeHierarchyConfig = (hierarchyConfig) => {
  if (!isValidHierarchyConfig(hierarchyConfig)) {
    return ["initiative"];
  }
  
  // Garantir que iniciativa esteja sempre presente
  if (!hierarchyConfig.includes("initiative")) {
    return [...hierarchyConfig, "initiative"];
  }
  
  return hierarchyConfig;
};
